import React, { Component } from 'react';
import { compose, graphql } from 'react-apollo';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import get from 'lodash/get';

import Button from 'shared/Button';
import DisclaimerModal from './DisclaimerModal';
import UserAccountTypeSwitch from 'apps/ask/components/UserAccountTypeSwitch';

import { withAppContext } from '../contexts/app';
import { withClinicalData } from 'apps/ask/contexts/clinicalData';
import { signUpMutation } from 'apps/ask/mutations/sign-up';
import { EMAIL_REGEX } from 'apps/ask/data/constants';

import SegmentAnalytics from '../../../helpers/SegmentAnalytics.js';

class SignUpForm extends Component {
  constructor(props) {
    super(props);
    this.state = {
      name: '',
      email: '',
      password: '',
      error: '',
      accountType: 'patient',
      isValid: false,
      acceptedTerms: false,
      isSubmitting: false,
      acceptedDisclaimer: false,
      disclaimerModalIsOpen: false
    };
  }

  handleInputChange = (name) => (event) => {
    this.setState({ [name]: event.target.value }, this.validateForm);
  }

  handleAcceptedTermsChange = () => {
    this.setState(({ acceptedTerms }) => ({ acceptedTerms: !acceptedTerms }), this.validateForm);
  }

  handleSignInClick = (event) => {
    event.preventDefault();
    this.props.onSignInClick();
  }

  validateForm() {
    this.setState({ isValid: this.isFormValid() });
  }

  isFormValid() {
    const { name, email, password, acceptedTerms } = this.state;

    if (!email || !EMAIL_REGEX.test(email)) return false;
    if (!name) return false;
    if (!password) return false;
    if (!acceptedTerms) return false;

    return true;
  }

  createAccount = async () => {
    const { accountType, name, email, password } = this.state;
    const { signUpMutation, onSignUp } = this.props;
    const standardError = 'An unexpected error occured';

    try {
      this.setState({ isSubmitting: true, error: '' });

      const recaptchaToken = await this.getCaptchaToken();
      const response = await signUpMutation({
        variables: {
          input: {
            accountType,
            name,
            email,
            password,
            recaptchaToken
          }
        }
      });

      const result = get(response, 'data.signUp');

      this.setState({ isSubmitting: false });

      if (result.success) {
        SegmentAnalytics.track('New sign up', {
          accountType: accountType,
          //needed for compatibility with GA
          label: accountType
        });

        onSignUp(result.user);
      } else {
        SegmentAnalytics.track('Sign up error', {
          error: result.notice
        });

        this.setState({ error: result.notice || standardError });
      }
    } catch (error) {
      this.setState({ error: standardError, isSubmitting: false });
    }
  }

  async getCaptchaToken() {
    const { app } = this.props;

    return grecaptcha
      .execute(app.recaptchaSiteKey, { action: 'sign_up' });
  }

  handleSubmit = async (event) => {
    if (event) { event.preventDefault(); }

    const { isValid, isSubmitting, acceptedDisclaimer } = this.state;

    if (!isValid || isSubmitting) return;

    if (acceptedDisclaimer) {
      this.createAccount();
    } else {
      SegmentAnalytics.track('Signup disclaimer showed');
      this.setState({ disclaimerModalIsOpen: true });
    }
  }

  async handleDisclaimerModalChoice(userAccepted) {
    // User made a choice on the disclaimer modal (Agree | Disagree)
    await this.setState({
      acceptedDisclaimer: userAccepted,
      disclaimerModalIsOpen: false
    });
    SegmentAnalytics.track('Signup disclaimer response', { userAccepted });

    if (userAccepted) {
      this.handleSubmit();
    } else {
      this.props.onModalClosed();
    }
  }

  handleDisclaimerModalClosed() {
    // User closed the modal by either clicking the backdrop or top right X button
    this.setState({ disclaimerModalIsOpen: false });
  }

  render() {
    // Will always return false because ATM the CreateAccount screen is shown before we actually have results
    const hasResults = Object.entries(this.props.clinicalData.inference).length > 0;

    const { isSubmitting, error, accountType } = this.state;
    const { showSignInLink } = this.props;

    return (
      <React.Fragment>
        <form className="sign-up-form" onSubmit={this.handleSubmit} noValidate>
          <h3>Welcome to Triage</h3>

          {error && (
            <span className="form-message error">{ error }</span>
          )}

          <div className="form-row name-email">
            <div className="form-group">
              <label htmlFor="name">Name</label>
              <input
                name="name"
                type="text"
                placeholder="Type your name"
                value={this.state.name}
                onChange={this.handleInputChange('name')}
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                name="email"
                type="email"
                placeholder="Type your email"
                value={this.state.email}
                onChange={this.handleInputChange('email')}
              />
            </div>
          </div>

          <div className="form-row account-type-password">
            <div className="form-group account-type">
              <label>Account Type</label>

              <UserAccountTypeSwitch value={accountType} onChange={(value) => { this.setState({ accountType: value }); }} />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                name="password"
                type="password"
                placeholder="Type your password"
                value={this.state.password}
                onChange={this.handleInputChange('password')}
              />
            </div>
          </div>

          {showSignInLink && (
            <p className="sign-up-form__signin-link">
              Have an account? <a href="#" onClick={this.handleSignInClick}>Sign in here.</a>
            </p>
          )}

          <div className="form-group">
            <input
              className="checkbox"
              type="checkbox"
              id="acceptedTerms"
              checked={this.state.acceptedTerms}
              onChange={this.handleAcceptedTermsChange}
            />
            <label className="terms-label checkbox-label" htmlFor="acceptedTerms">
              I agree to the <Link to="/terms" target="_blank">Terms of Service</Link> and <Link to="/terms#privacy_policy" target="_blank">Privacy Policy</Link>.
            </label>
          </div>

          <Button
            className={`${showSignInLink ? 'padded' : ''}`}
            type="submit"
            disabled={!this.state.isValid}
            showSpinner={isSubmitting}
          >
            { isSubmitting ? 'Signing up...' : `Sign up${hasResults ? ' & view results' : ''}` }
          </Button>
        </form>

        { this.state.disclaimerModalIsOpen &&
          <DisclaimerModal
            onAgree={this.handleDisclaimerModalChoice.bind(this, true)}
            onDisagree={this.handleDisclaimerModalChoice.bind(this, false)}
            onModalClosed={this.handleDisclaimerModalClosed.bind(this)} />
        }
      </React.Fragment>
    );
  }
}

SignUpForm.defaultProps = {
  showSignInLink: true,
  onModalClosed: () => {}
};

SignUpForm.propTypes = {
  app: PropTypes.object.isRequired,
  clinicalData: PropTypes.object.isRequired,
  onSignUp: PropTypes.func.isRequired,
  onSignInClick: PropTypes.func,
  signUpMutation: PropTypes.func.isRequired,
  showSignInLink: PropTypes.bool.isRequired,
  onModalClosed: PropTypes.func
};

export default compose(
  graphql(signUpMutation, {
    name : 'signUpMutation'
  }),
  withAppContext
)(withClinicalData(SignUpForm));
